import Phaser from 'phaser';
import { JAR_CAPACITY } from '../config.ts';
import { Audio } from '../audio.ts';

const COIN_D = 30; // mini-coin diameter inside the jar
const PER_ROW = 3;
const ROW_DY = 24;

/**
 * A jar on the counter that visibly fills with coins as the shop earns — a
 * wordless progress reward. Every happy customer drops a coin in; filling it
 * triggers a little celebration.
 */
export class CoinJar extends Phaser.GameObjects.Container {
  private coins: Phaser.GameObjects.Image[] = [];
  private jarImg: Phaser.GameObjects.Image;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    this.jarImg = scene.add.image(0, 0, 'jar').setDisplaySize(180, 220);
    this.add(this.jarImg);
    scene.add.existing(this);
  }

  get filledCount(): number {
    return this.coins.length;
  }

  /** Drop one coin into the jar with a little plink. */
  addCoin(delayMs = 0): void {
    const idx = this.coins.length % JAR_CAPACITY;
    const row = Math.floor(idx / PER_ROW);
    const col = idx % PER_ROW;
    const inRow = Math.min(PER_ROW, JAR_CAPACITY - row * PER_ROW);
    const rowW = (inRow - 1) * (COIN_D - 4);
    const x = -rowW / 2 + col * (COIN_D - 4);
    const y = 86 - row * ROW_DY;

    const coin = this.scene.add.image(x, -150, 'coin_5').setDisplaySize(COIN_D, COIN_D);
    this.add(coin);
    this.bringToTop(this.jarImg); // glass stays in front so coins look "inside"
    this.coins.push(coin);

    this.scene.time.delayedCall(delayMs, () => {
      Audio.coin();
      this.scene.tweens.add({
        targets: coin,
        y,
        duration: 480,
        ease: 'Bounce.Out',
      });
    });

    if (this.coins.length >= JAR_CAPACITY) {
      this.scene.time.delayedCall(delayMs + 520, () => this.celebrate());
    }
  }

  private celebrate(): void {
    Audio.fanfare();
    this.scene.tweens.add({
      targets: this,
      scale: { from: 1, to: 1.12 },
      duration: 220,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.InOut',
    });
    // Reset so the reward can be earned again — never a dead end.
    this.scene.time.delayedCall(1400, () => {
      this.coins.forEach((c, i) =>
        this.scene.tweens.add({
          targets: c,
          y: -160,
          alpha: 0,
          duration: 300,
          delay: i * 20,
          onComplete: () => c.destroy(),
        }),
      );
      this.scene.time.delayedCall(1000, () => (this.coins = []));
    });
  }
}
